import type {
  AIAssistantPrototypeSurface,
  AIAssistantPrototypeUtilityTab,
  AIAssistantPrototypeWidthMode,
} from "@triedge/ai-assistant-ui";
import type { ReportGenerationWorkspaceTab } from "../../report-generation/reportGenerationWorkspaceSlice";

export const ASSISTANT_PROOF_PRESET_QUERY_PARAM = "__assistantProofPreset";
export const REPORT_PROOF_PRESET_QUERY_PARAM = "__reportProofPreset";

export type AssistantPanelV2ProofPreviewMode = "inline-summary" | "workspace";

export type AssistantPanelV2ProofPreset =
  | "rail-route-start"
  | "rail-streaming"
  | "rail-settled"
  | "rail-closed"
  | "rail-model-menu"
  | "rail-width-menu"
  | "rail-settled-expanded"
  | "rail-settled-half"
  | "full-welcome"
  | "full-chat"
  | "full-model-menu"
  | "full-width-menu"
  | "full-files"
  | "full-threads"
  | "full-preview";

export type AssistantPanelV2ProofState = {
  panelVisibility?: "open" | "closed";
  widthMode?: AIAssistantPrototypeWidthMode;
  surface?: AIAssistantPrototypeSurface;
  utilityTab?: AIAssistantPrototypeUtilityTab;
  utilityPaneOpen?: boolean;
  widthMenuOpen?: boolean;
  modelMenuOpen?: boolean;
  previewPane?: AssistantPanelV2ProofPreviewMode | null;
};

export const ASSISTANT_PANEL_V2_PROOF_PRESETS: Record<
  AssistantPanelV2ProofPreset,
  AssistantPanelV2ProofState
> = {
  "rail-route-start": {
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "rail-streaming": {
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "rail-settled": {
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "rail-closed": {
    panelVisibility: "closed",
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "rail-model-menu": {
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    modelMenuOpen: true,
    previewPane: null,
  },
  "rail-width-menu": {
    widthMode: "compact",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: true,
    previewPane: null,
  },
  "rail-settled-expanded": {
    widthMode: "expanded",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "rail-settled-half": {
    widthMode: "half",
    surface: "rail-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "full-welcome": {
    widthMode: "full",
    surface: "full-welcome",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "full-chat": {
    widthMode: "full",
    surface: "full-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    previewPane: null,
  },
  "full-model-menu": {
    widthMode: "full",
    surface: "full-chat",
    utilityPaneOpen: false,
    widthMenuOpen: false,
    modelMenuOpen: true,
    previewPane: null,
  },
  "full-width-menu": {
    widthMode: "full",
    surface: "full-chat",
    utilityPaneOpen: false,
    widthMenuOpen: true,
    previewPane: null,
  },
  "full-files": {
    widthMode: "full",
    surface: "full-chat",
    utilityTab: "files",
    utilityPaneOpen: true,
    widthMenuOpen: false,
    previewPane: null,
  },
  "full-threads": {
    widthMode: "full",
    surface: "full-chat",
    utilityTab: "threads",
    utilityPaneOpen: true,
    widthMenuOpen: false,
    previewPane: null,
  },
  "full-preview": {
    widthMode: "full",
    surface: "full-chat",
    utilityTab: "files",
    utilityPaneOpen: true,
    widthMenuOpen: false,
    previewPane: "inline-summary",
  },
};

export type ReportGenerationProofPreset =
  | "templates-gallery"
  | "templates-my"
  | "templates-version-history"
  | "filters-empty"
  | "filters-configured"
  | "history-list"
  | "preview-empty"
  | "preview-run"
  | "preview-export"
  | "preview-column-menu"
  | "left-panel-collapsed";

export type ReportGenerationProofTemplateView = "gallery" | "my";

export type ReportGenerationProofState = {
  collapsed?: boolean;
  activeTab?: ReportGenerationWorkspaceTab;
  templateView?: ReportGenerationProofTemplateView;
  versionHistoryTemplateName?: string | null;
  historyTemplateName?: string | null;
  reportTitle?: string;
  runReport?: boolean;
  showExport?: boolean;
  contextMenuFieldLabel?: string | null;
};

export const REPORT_GENERATION_PROOF_PRESETS: Record<
  ReportGenerationProofPreset,
  ReportGenerationProofState
> = {
  "templates-gallery": {
    activeTab: "templates",
    templateView: "gallery",
    reportTitle: "Tuition report",
  },
  "templates-my": {
    activeTab: "templates",
    templateView: "my",
    reportTitle: "Tuition report",
  },
  "templates-version-history": {
    activeTab: "templates",
    templateView: "my",
    versionHistoryTemplateName: "Aging Report",
    reportTitle: "Tuition report",
  },
  "filters-empty": {
    activeTab: "filters",
    reportTitle: "Tuition report",
    runReport: false,
  },
  "filters-configured": {
    activeTab: "filters",
    reportTitle: "Aging Report",
    runReport: true,
  },
  "history-list": {
    activeTab: "history",
    historyTemplateName: "Aging Report",
    reportTitle: "Aging Report",
  },
  "preview-empty": {
    activeTab: "templates",
    reportTitle: "Tuition report",
    runReport: false,
  },
  "preview-run": {
    activeTab: "filters",
    reportTitle: "Aging Report",
    runReport: true,
  },
  "preview-export": {
    activeTab: "filters",
    reportTitle: "Aging Report",
    runReport: true,
    showExport: true,
  },
  "preview-column-menu": {
    activeTab: "filters",
    reportTitle: "Aging Report",
    runReport: true,
    contextMenuFieldLabel: "Family name",
  },
  "left-panel-collapsed": {
    activeTab: "templates",
    collapsed: true,
    reportTitle: "Tuition report",
  },
};

const isPrototypeWidthMode = (
  value: unknown,
): value is AIAssistantPrototypeWidthMode =>
  value === "compact" ||
  value === "expanded" ||
  value === "half" ||
  value === "full";

const isPrototypeSurface = (
  value: unknown,
): value is AIAssistantPrototypeSurface =>
  value === "rail-chat" ||
  value === "full-welcome" ||
  value === "full-chat" ||
  value === "full-files" ||
  value === "full-threads" ||
  value === "full-preview";

const isPrototypeUtilityTab = (
  value: unknown,
): value is AIAssistantPrototypeUtilityTab =>
  value === "files" || value === "threads";

const isPrototypePanelVisibility = (
  value: unknown,
): value is "open" | "closed" => value === "open" || value === "closed";

export const isAssistantPanelV2ProofPreset = (
  value: unknown,
): value is AssistantPanelV2ProofPreset =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(ASSISTANT_PANEL_V2_PROOF_PRESETS, value);

export const normalizeAssistantProofPreviewMode = (
  value: unknown,
): AssistantPanelV2ProofPreviewMode | null | undefined => {
  if (value === null) {
    return null;
  }
  if (value === true || value === "inline-summary" || value === "default") {
    return "inline-summary";
  }
  if (value === "workspace") {
    return "workspace";
  }
  return undefined;
};

export const buildAssistantPanelV2ProofState = (
  candidate: unknown,
): AssistantPanelV2ProofState | null => {
  if (isAssistantPanelV2ProofPreset(candidate)) {
    return ASSISTANT_PANEL_V2_PROOF_PRESETS[candidate];
  }
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const record = candidate as Record<string, unknown>;
  const presetState = isAssistantPanelV2ProofPreset(record.preset)
    ? ASSISTANT_PANEL_V2_PROOF_PRESETS[record.preset]
    : null;
  const previewPane = normalizeAssistantProofPreviewMode(record.previewPane);
  const nextState: AssistantPanelV2ProofState = {
    panelVisibility: isPrototypePanelVisibility(record.panelVisibility)
      ? record.panelVisibility
      : presetState?.panelVisibility,
    widthMode: isPrototypeWidthMode(record.widthMode)
      ? record.widthMode
      : presetState?.widthMode,
    surface: isPrototypeSurface(record.surface)
      ? record.surface
      : presetState?.surface,
    utilityTab: isPrototypeUtilityTab(record.utilityTab)
      ? record.utilityTab
      : presetState?.utilityTab,
    utilityPaneOpen:
      typeof record.utilityPaneOpen === "boolean"
        ? record.utilityPaneOpen
        : presetState?.utilityPaneOpen,
    widthMenuOpen:
      typeof record.widthMenuOpen === "boolean"
        ? record.widthMenuOpen
        : presetState?.widthMenuOpen,
    modelMenuOpen:
      typeof record.modelMenuOpen === "boolean"
        ? record.modelMenuOpen
        : presetState?.modelMenuOpen,
    previewPane:
      previewPane === undefined ? presetState?.previewPane : previewPane,
  };

  return Object.values(nextState).some((value) => value !== undefined)
    ? nextState
    : null;
};

export const parseAssistantPanelV2ProofState = (
  rawValue: string | null,
): AssistantPanelV2ProofState | null => {
  if (!rawValue) {
    return null;
  }
  try {
    return buildAssistantPanelV2ProofState(JSON.parse(rawValue));
  } catch {
    return buildAssistantPanelV2ProofState(rawValue);
  }
};

export const parseAssistantPanelV2ProofStateFromSearch = (
  search: string,
): AssistantPanelV2ProofState | null => {
  const params = new URLSearchParams(search);
  return parseAssistantPanelV2ProofState(
    params.get(ASSISTANT_PROOF_PRESET_QUERY_PARAM),
  );
};

export const resolveAssistantPrototypeThreadReturnSurface = (
  surface: AIAssistantPrototypeSurface | undefined,
  widthMode: AIAssistantPrototypeWidthMode | undefined,
): AIAssistantPrototypeSurface => {
  if (surface === "rail-chat") {
    return "rail-chat";
  }
  if (surface === "full-chat") {
    return "full-chat";
  }
  if (
    surface === "full-welcome" ||
    surface === "full-files" ||
    surface === "full-threads" ||
    surface === "full-preview"
  ) {
    return "full-chat";
  }
  return widthMode === "full" ? "full-chat" : "rail-chat";
};

export const closeAssistantPrototypeThreadTray = (
  state: AssistantPanelV2ProofState,
): AssistantPanelV2ProofState => ({
  ...state,
  surface: resolveAssistantPrototypeThreadReturnSurface(
    state.surface,
    state.widthMode,
  ),
  utilityPaneOpen: false,
});

export const isReportGenerationProofPreset = (
  value: unknown,
): value is ReportGenerationProofPreset =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(REPORT_GENERATION_PROOF_PRESETS, value);

export const parseReportGenerationProofPresetFromSearch = (
  search: string,
): ReportGenerationProofPreset | null => {
  const params = new URLSearchParams(search);
  const preset = params.get(REPORT_PROOF_PRESET_QUERY_PARAM);
  return isReportGenerationProofPreset(preset) ? preset : null;
};
